import { Link } from "react-router-dom";
import { BetaBadge } from "./BetaBadge";

/**
 * Kalib AI — Preparing screen (Screen 9 of the revised setup flow).
 *
 * Spec rules:
 *   Headline:  "Preparing your Account Reveals"
 *   Subcopy:   "Applying your commercial lens to the latest public signals."
 *   Behaviour: Always plural. Never names a specific account.
 *              Optional small chips of selected account names side-by-side, dimmed.
 *              No progress bars per account. No "AI is thinking" theatrics.
 *              No spinners with marketing copy.
 *
 * The new prop is `accountNames` (an array). The legacy `accountName`
 * (single string) prop is accepted for back-compat with callers that
 * still pass it; it's converted to a one-item array internally.
 */
export default function LoadingReveal({ accountNames = [], accountName = "" }) {
  const names = Array.isArray(accountNames) && accountNames.length
    ? accountNames.filter(Boolean)
    : (accountName ? [accountName] : []);

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="loading-reveal">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="text-xl font-semibold tracking-tight text-zinc-950 font-heading"
              data-testid="loading-logo-link"
            >
              Kalib AI
            </Link>
            <BetaBadge />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h2
            className="text-2xl sm:text-3xl font-medium tracking-tight text-zinc-950 font-heading mb-3"
            data-testid="loading-headline"
          >
            Preparing your Account Reveals
          </h2>
          <p
            className="text-sm text-zinc-900"
            data-testid="loading-subhead"
          >
            Applying your commercial lens to the latest public signals.
          </p>

          {/* Optional dimmed account chips — names only, no progress per chip,
              no spinners. Side-by-side, neutral colour. */}
          {names.length > 0 && (
            <div
              className="mt-7 flex flex-wrap items-center justify-center gap-2"
              data-testid="loading-account-chips"
            >
              {names.map((n) => (
                <span
                  key={n}
                  className="px-3 py-1 text-[12px] text-zinc-500 bg-white border border-zinc-200"
                  data-testid={`loading-account-chip-${n.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n}
                </span>
              ))}
            </div>
          )}

          {/* Single subtle global pulse — no progress bar, no percentages. */}
          <div className="mt-9 flex items-center justify-center gap-1.5" aria-hidden="true">
            <span className="w-1.5 h-1.5 bg-amber-700 animate-pulse-dot rounded-full" />
            <span className="w-1.5 h-1.5 bg-amber-700 animate-pulse-dot rounded-full" style={{ animationDelay: "0.2s" }} />
            <span className="w-1.5 h-1.5 bg-amber-700 animate-pulse-dot rounded-full" style={{ animationDelay: "0.4s" }} />
          </div>
        </div>
      </div>
    </div>
  );
}
